import React, { useState, useEffect, useMemo } from 'react';
import {
    EuiPanel,
    EuiText,
    EuiFlexGroup,
    EuiFlexItem,
    EuiButton,
    EuiButtonEmpty,
    EuiSpacer,
    EuiCheckbox,
    EuiAccordion,
    EuiBadge,
    EuiFieldSearch,
    EuiHorizontalRule,
    EuiLoadingSpinner,
    EuiIcon,
    EuiCallOut,
} from '@elastic/eui';
import {
    groupOutletsByCity,
    initializeCitySelection,
    toggleCitySelection,
    toggleOutletSelection,
    getSelectedOutletIds,
    getSelectionSummary,
    selectAllOutlets,
    deselectAllOutlets,
    filterOutletsBySearch,
    getOutletDisplayName,
    getOutletId,
} from './cityOutletHelper';

const accordionStyles = `
    .cityAccordion .euiAccordion__childWrapper {
        transition: none !important;
    }
    .cityAccordion .euiAccordion__button {
        padding: 12px 20px;
        background: transparent;
    }
    .cityAccordion .euiAccordion__button:hover {
        background-color: #f3f4f6;
    }
    .cityAccordion .euiAccordion__iconWrapper {
        margin-right: 8px;
    }
`;

export const CityOutletSelector = ({ outlets, languageId, onSelectionChange, initialSelectedIds = [], loading = false }) => {
    const [citySelection, setCitySelection] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCities, setExpandedCities] = useState({});

    // Group outlets by city
    const groupedOutlets = useMemo(() => {
        return groupOutletsByCity(outlets, languageId || '1');
    }, [outlets, languageId]);

    // Filter outlets based on search
    const filteredGroupedOutlets = useMemo(() => {
        return filterOutletsBySearch(groupedOutlets, searchTerm, languageId || '1');
    }, [groupedOutlets, searchTerm, languageId]);

    // Initialize selection state
    useEffect(() => {
        if (outlets && outlets.length > 0) {
            const initialSelection = initializeCitySelection(groupedOutlets, initialSelectedIds);
            setCitySelection(initialSelection);
        }
    }, [outlets, groupedOutlets]);

    // Notify parent of selection changes
    useEffect(() => {
        const selectedIds = getSelectedOutletIds(citySelection);
        onSelectionChange(selectedIds);
    }, [citySelection, onSelectionChange]);

    const handleCityToggle = (city, checked) => {
        setCitySelection(prevState => toggleCitySelection(prevState, city, checked));
    };

    const handleOutletToggle = (city, outletId, checked) => {
        setCitySelection(prevState => toggleOutletSelection(prevState, city, outletId, checked));
    };

    const handleSelectAll = () => {
        setCitySelection(selectAllOutlets(citySelection));
        // Expand all cities when selecting all
        const allExpanded = {};
        Object.keys(filteredGroupedOutlets).forEach(city => {
            allExpanded[city] = true;
        });
        setExpandedCities(allExpanded);
    };

    const handleDeselectAll = () => {
        setCitySelection(deselectAllOutlets(citySelection));
    };

    const toggleCityExpansion = (city) => {
        setExpandedCities(prev => ({
            ...prev,
            [city]: !prev[city]
        }));
    };

    const summary = getSelectionSummary(citySelection, groupedOutlets);

    if (loading) {
        return (
            <EuiPanel paddingSize="l" hasShadow={false} style={{ backgroundColor: '#f5f7fa', borderRadius: '6px', minHeight: '200px' }}>
                <EuiFlexGroup justifyContent="center" alignItems="center" style={{ height: '200px' }}>
                    <EuiFlexItem grow={false}>
                        <div style={{ textAlign: 'center' }}>
                            <EuiLoadingSpinner size="l" />
                            <EuiSpacer size="m" />
                            <EuiText size="s" color="subdued">
                                Loading outlets...
                            </EuiText>
                        </div>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiPanel>
        );
    }

    if (!outlets || outlets.length === 0) {
        return (
            <EuiCallOut
                title="No outlets available"
                color="warning"
                iconType="alert"
            >
                <p>There are no outlets configured for this restaurant.</p>
            </EuiCallOut>
        );
    }

    const renderCityCheckbox = (city) => {
        const cityData = citySelection[city];
        if (!cityData) return null;

        const isPartial = cityData.selected === 'partial';
        const isChecked = cityData.selected === true || isPartial;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <EuiCheckbox
                    id={`city-${city}`}
                    label=""
                    checked={isChecked}
                    indeterminate={isPartial}
                    onChange={(e) => handleCityToggle(city, e.target.checked)}
                    style={{ marginTop: '2px' }}
                />
                <EuiText size="s" style={{ fontWeight: 600, color: '#1a1a1a', fontSize: '12px' }}>
                    {city}
                </EuiText>
                {isPartial && (
                    <EuiIcon type="minus" color="warning" size="s" />
                )}
            </div>
        );
    };

    return (
        <>
            <style>{accordionStyles}</style>
            <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e0e5eb' }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #e0e5eb' }}>
                <EuiFlexGroup justifyContent="spaceBetween" alignItems="center" gutterSize="m">
                    <EuiFlexItem grow={false}>
                        <EuiText size="m">
                            <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#1a1a1a' }}>Select Target Outlets</h4>
                        </EuiText>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiFlexGroup gutterSize="s" alignItems="center">
                            <EuiFlexItem grow={false}>
                                <EuiButton
                                    size="s"
                                    fill
                                    color="primary"
                                    onClick={handleSelectAll}
                                    disabled={summary.selectedOutlets === summary.totalOutlets}
                                    style={{ maxHeight: '26px', padding: '0 12px' }}
                                >
                                    Select All
                                </EuiButton>
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <EuiButtonEmpty
                                    size="s"
                                    onClick={handleDeselectAll}
                                    disabled={summary.selectedOutlets === 0}
                                    style={{ maxHeight: '26px', padding: '0 12px' }}
                                >
                                    Deselect All
                                </EuiButtonEmpty>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </EuiFlexItem>
                </EuiFlexGroup>

                <EuiFlexGroup alignItems="center" gutterSize="m" style={{ marginTop: '6px' }}>
                    <EuiFlexItem grow={false}>
                        <div style={{ 
                            background: '#e6f3ff', 
                            color: '#0061c7', 
                            padding: '4px 12px', 
                            borderRadius: '4px', 
                            fontSize: '10px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <EuiIcon type="check" size="s" />
                            {summary.selectedOutlets} of {summary.totalOutlets} outlets selected
                        </div>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <div style={{ 
                            background: summary.partialCities > 0 ? '#fff6e6' : '#f3f4f6', 
                            color: summary.partialCities > 0 ? '#d97706' : '#6b7280', 
                            padding: '4px 12px', 
                            borderRadius: '4px', 
                            fontSize: '10px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <EuiIcon type="mapMarker" size="s" />
                            {summary.selectedCities} of {summary.totalCities} cities
                            {summary.partialCities > 0 && ` (+${summary.partialCities} partial)`}
                        </div>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </div>

            <div style={{ padding: '0px 12px 8px 12px', borderBottom: '1px solid #e0e5eb' }}>
                <EuiFieldSearch
                    placeholder="Search cities or outlets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    isClearable={true}
                    aria-label="Search cities and outlets"
                    fullWidth
                    compressed
                />
            </div>

            <div style={{ padding: '0' }}>
                {Object.keys(filteredGroupedOutlets).length === 0 ? (
                    <div style={{ padding: '20px 12px', textAlign: 'center' }}>
                        <EuiIcon type="search" size="xl" color="subdued" />
                        <EuiSpacer size="m" />
                        <EuiText size="m" color="subdued">
                            <p style={{ margin: 0 }}>No cities or outlets match "{searchTerm}"</p>
                        </EuiText>
                    </div>
                ) : (
                <div style={{ maxHeight: '380px', overflowY: 'auto', padding: '0' }}>
                    {Object.entries(filteredGroupedOutlets).map(([city, cityOutlets], index) => {
                        const cityData = citySelection[city];
                        if (!cityData) return null;

                        const selectedCount = Object.values(cityData.outlets).filter(Boolean).length;
                        const isExpanded = expandedCities[city];

                        return (
                            <React.Fragment key={city}>
                                <div style={{ 
                                    marginTop: '2px',
                                    borderBottom: '1px solid #e0e5eb'
                                }}>
                                    <EuiAccordion
                                        id={`city-accordion-${city}`}
                                        buttonContent={renderCityCheckbox(city)}
                                        extraAction={
                                            <EuiBadge color={selectedCount > 0 ? 'primary' : 'hollow'} style={{fontSize: '10px'}}>
                                                {selectedCount}/{cityOutlets.length} outlets
                                            </EuiBadge>
                                        }
                                        forceState={isExpanded ? 'open' : 'closed'}
                                        onToggle={() => toggleCityExpansion(city)}
                                        paddingSize="s"
                                        style={{ marginBottom: isExpanded ? '8px' : '0', borderBottom: isExpanded ? '1px solid #e3e4e8' : 'none' }}
                                    >
                                    <div style={{ backgroundColor: '#f8f9fa', padding: '8px 0' }}>
                                        {cityOutlets.map((outlet, outletIndex) => {
                                            const outletId = getOutletId(outlet);
                                            const outletName = getOutletDisplayName(outlet, languageId);
                                            const isSelected = cityData.outlets[outletId];

                                            return (
                                                <div key={outletId} style={{ 
                                                    paddingLeft: '42px', 
                                                    paddingRight: '20px',
                                                    paddingTop: outletIndex === 0 ? '0' : '2px',
                                                    paddingBottom: outletIndex === cityOutlets.length - 1 ? '0' : '4px'
                                                }}>
                                                    <EuiCheckbox
                                                        id={`outlet-${outletId}`}
                                                        label={
                                                            <span style={{
                                                                fontSize: '10px',
                                                                color: isSelected ? '#1a1a1a' : '#6b7280',
                                                                fontWeight: isSelected ? 500 : 400
                                                            }}>
                                                                {outletName}
                                                            </span>
                                                        }
                                                        checked={isSelected}
                                                        onChange={(e) => handleOutletToggle(city, outletId, e.target.checked)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </EuiAccordion>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                )}
            </div>

            {summary.selectedOutlets === 0 && (
                <div style={{ 
                    padding: '16px 20px', 
                    backgroundColor: '#fff7ed',
                    borderTop: '1px solid #fed7aa',
                    borderBottomLeftRadius: '8px',
                    borderBottomRightRadius: '8px'
                }}>
                    <EuiFlexGroup alignItems="center" gutterSize="s">
                        <EuiFlexItem grow={false}>
                            <EuiIcon type="alert" color="warning" size="m" />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiText size="s" style={{ color: '#c2410c', fontWeight: 500 }}>
                                No outlets selected
                            </EuiText>
                            <EuiText size="xs" color="subdued">
                                When no outlets are selected, the notification will be sent to all outlets by default.
                            </EuiText>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </div>
            )}
        </div>
        </>
    );
};